import { AppBar, Tab, Tabs } from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { BreadcrumbsItem } from "react-breadcrumbs-dynamic";
import { Redirect, Route, Switch, useHistory, useRouteMatch } from "react-router-dom";
import Panel from "../../../components/Panel";
import FinalCustomer from "../../../model/FinalCustomer";
import Task from "../../../model/Task";
import { useAuthentication } from "../../../reducer/authentication";
import { useLoading } from "../../../reducer/loading";
import { ServiceApi } from "../../../service/ServiceApiWithLoading";
import TaskApi from "../../../service/TaskApi";
import { delay } from "../../../utils";
import RouteIdParams from "../../System/RouteIdParams";
import TicketsForm, { TicketsFormData } from "./TicketsForm";
import eventEmitter from "../../../components/SafeDeskEventProvider";

const api = new TaskApi();

type TicketsEditTabValue = 'general';
type TicketsEditTabLabel = 'Geral';
type TicketsEditTab = { label: TicketsEditTabLabel, value: TicketsEditTabValue };

interface TicketsEditTabsProps {
    value?: TicketsEditTab | string;
    onTabChange(tab: TicketsEditTab): void;
    editing: boolean;
}

function TicketsEditTabs(props: TicketsEditTabsProps) {
    const { value, editing, onTabChange } = props;

    const tabs: TicketsEditTab[] = [
        { label: 'Geral', value: 'general' }
    ];

    return (
        <Tabs
            value={value}
            onChange={(ev, v) => onTabChange(v)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
        >
            {tabs.map((ob) => {
                const disabled = editing && ob.value !== value;
                return <Tab
                    key={ob.value}
                    value={ob.value}
                    disabled={disabled}
                    //title={disabled ? t('saveOrUndoBeforeSwitchingTabs', { capitalize: true }) : ''}
                    label={ob.label}
                />
            })}
        </Tabs>
    );
}

interface TicketsEditProps {
    
}

const apiFinalCustomer = new ServiceApi<FinalCustomer>('finalCustomer');

export default function TicketsEdit(props: TicketsEditProps) {
    const match = useRouteMatch<RouteIdParams>();
    const id = match.params.id;
    const { user } = useAuthentication();
    const setLoading = useLoading();

    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();

    const [tabKey, setTabKey] = useState<number>(0);
    const [editing, setEditing] = useState<boolean>(false);
    const [item, setItem] = useState<Task>();
    const [commboxId, setCommboxId] = useState<number | null>(null);

    function loadItem() {
        return fetchItem().then((nd) => {
            apiFinalCustomer.getList({ where: [['name', '=', 'Commbox']] }).then((finalCustomerList) => {
                if (finalCustomerList.length > 0) {
                    setCommboxId(finalCustomerList[0].id);
                }
            });
            
        });
    }

    useEffect(() => {
        loadItem()
    }, [id]);

    useEffect(() => {
        if(item && user){
            api.markViewedComments(item.id, user.id).then((b) => {
                eventEmitter.emit('commentResolved', {id: 1});
            });
        }
    }, [item]);

    function fetchItem() {
        return api.getById(id, {
            relations: ['products', 'comments', 'comments.time_counter', 'comments.comment_task_users', 'comments.comment_task_users.user', 'comments.attachments', 'comments.attachments.system_attachment', 'attachments', 'attachments.system_attachment',
                'created_by_user', 'customer_contacts', 'final_customer', 'observers', 'tags']
        }).then(setItem);
    }

    function onTabChange(tab: TicketsEditTab) {
        loadItem().then(() => {
            history.replace(`${match.url}/${tab}`);
        });
    }

    async function onSubmit(values: TicketsFormData, isDirty?: boolean, submitType?: string | null) {
        setLoading(true);
        try {
            const result = await api.update(id, {
                name: values.name,
                description: values.description,
                final_customer_id: item?.final_customer_id,
                ticket_type_id: item?.ticket_type_id
            });
            if (!result) throw new Error('Erro ao salvar chamado');
            await api.updateCustomerContacts(id, values.customer_contacts);
            const relationCommentIdxId: { idx: number, id: number }[] = [];
            if (values.comments && values.comments.length > 0) {
                await Promise.all(values.comments.map(async (comment, index) => {
                    if (comment.id === 0 && comment.comment) {
                        let savedComment = await api.saveComment(id, comment.comment, comment.is_public, comment.user_id, comment.type, comment.time_counter?.hours, comment.time_counter?.minutes, comment.comment_task_users?.map((ctu) => ctu.user_id));
                        relationCommentIdxId.push({ idx: index, id: savedComment.id });
                    }
                    return;
                }))
            }
            if (values.attachments && values.attachments.length > 0) {
                for (const attachment of values.attachments) {
                    if (attachment.id > 0) {
                        if(attachment.edited === true){
                            await api.updateAttachment(attachment.id, attachment.description, attachment.type);
                        }                        
                    } else if (attachment.file || attachment.system_attachment_id) {
                        let commentRelItem = relationCommentIdxId.find(o => o.idx === attachment.commentIdx)
                        await api.saveAttachment(attachment.file || null, id, attachment.description, attachment.type, commentRelItem?.id, attachment.system_attachment_id);
                    }
                }
            }
            if (relationCommentIdxId.length > 0) {
                await api.sendCommentsEmail(relationCommentIdxId.map(o => o.id));
            }
            enqueueSnackbar('Chamado salvo com sucesso!', { variant: 'success' });
            await delay(100);
            if (submitType === 'save') {
                await fetchItem();
                onUndo();
            } else {
                onCancel();
            }
        } catch (e) {
            if (e instanceof Error) {
                enqueueSnackbar(e.message, { variant: 'error' });
            }
        } finally {
            setLoading(false);
        }
    }

    function onCancel() {
        if (history.length > 2) {
            history.goBack();
        } else {
            history.push(`/support/tickets/all`);
        }
    }

    function onUndo() {
        fetchItem().then((i) => {
            setEditing(false);
            setTabKey(tabKey + 1);
        });
    }

    if (!item || !commboxId) return null;

    const title: React.ReactNode = `Edição Chamado - ${item?.name}`;
    return (
        <>
            <BreadcrumbsItem to={match.url}>
                {title}
            </BreadcrumbsItem>
            <Panel panelHeaderTitle={title}>
                <Switch>
                    <Route path={`${match.url}/:tab`}>
                        {({ match }) => (
                            <>
                                <AppBar position="relative" color="default">
                                    <TicketsEditTabs
                                        value={match?.params.tab}
                                        onTabChange={onTabChange}
                                        editing={editing}
                                    />
                                </AppBar>
                                {match?.params.tab === 'general' && <TicketsForm
                                    title=''
                                    key={`general-${tabKey}`}
                                    item={item}
                                    onCancel={onCancel}
                                    onSubmit={onSubmit}
                                    onChange={setEditing}
                                    onUndo={onUndo}
                                />}
                            </>
                        )}
                    </Route>
                    <Route>
                        <Redirect to={`${match.url}/general`} />
                    </Route>
                </Switch>
            </Panel>
        </>
    );
}